import { memo, useMemo } from 'react';
import { ActuatorAccessoriesPageData } from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { UserSettings } from 'src/types/UserSettings.ts';
import { LocationState } from 'src/actuators/LocationState.ts';
import { prepareActuatorAttributes } from 'src/actuators/prepareActuatorAttributes.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { getProductUrl } from 'src/utils/getProductUrl.ts';
import { getExcelFileName } from 'src/utils/getExcelFileName.ts';
import { DownloadIcon } from 'src/modules/common/icons/DownloadIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { useComposeExcelFile } from 'src/common/hooks/useComposeExcelFile.ts';

type Props = {
    accessories: ActuatorAccessoriesPageData['accessories']['accessories'];
    actuator: ActuatorAccessoriesPageData['accessories']['actuator'];
    userSettings: UserSettings;
    locationState: LocationState;
    showFailSafe: boolean;
};
export const ActuatorExportExcel = memo(({
    accessories,
    actuator,
    userSettings,
    locationState,
    showFailSafe,
}: Props) => {
    const table = useMemo(() => {
        const attr = prepareActuatorAttributes(actuator, showFailSafe)
            .reduce(((a, v) => [...a, ...v]), []);

        return ({
            columns: [
                MESSAGES.exportExcelMaterial,
                MESSAGES.exportExcelQuantity,
                ...attr.map(([key]) => key),
                MESSAGES.Description,
            ],
            rows: [
                [
                    {
                        text: actuator.Label,
                        hyperlink: getProductUrl({
                            countryDomain: userSettings.countryDomain,
                            currentLocale: userSettings.currentLocale,
                            label: actuator.Label,
                        }),
                    },
                    String(locationState.qty),
                    ...attr.map(([, value]) => value),
                ],
                ...accessories.map((p) => [
                    { text: p.item.Label,
                        hyperlink: getProductUrl({
                            countryDomain: userSettings.countryDomain,
                            currentLocale: userSettings.currentLocale,
                            label: p.item.Label,
                        }) },
                    String(locationState.selectedAccessories.find((
                        { label },
                    ) => label === p.item.Label)?.qty),
                    ...attr.map(() => ''),
                    p.item.description,
                ]),
            ],
        });
    }, [showFailSafe, accessories, actuator, locationState, userSettings]);

    const downloadExcel = useComposeExcelFile({ table });

    return (
        <Button
            as="a"
            href={downloadExcel ?? ''}
            download={getExcelFileName()}
            loading={downloadExcel === null}
            className="vss-checkout-page__navigation-button"
            size="lg"
            kind="tertiary"
            icon={(
                <div className="vss-checkout-page__navigation-icon">
                    <DownloadIcon/>
                </div>
            )}
        >
            <I18n messageId={MESSAGES.exportExcel}/>
        </Button>
    );
});
